import { Trans, useTranslation } from 'react-i18next';
import { Stack, Paper, Typography, Grid, Avatar, IconButton } from '@mui/material';
import { PostCard } from '../dashboards/PostCard';
import { stringAvatar } from '@/utils/string-avatar';
import { numberFormat } from '@/utils/number-format';
import { useState } from 'react';
import { ArrowDropDownRounded, ArrowLeftRounded } from '@mui/icons-material';

type CompetitorProps = {
    profile: {
        picture: string;
        name: string;
        username?: string | null;
        followers: number;
        net: number;
    };
    topPosts: {
        id: string;
        profile: {
            picture: string;
            name: string;
            net: string;
        };
        caption: string;
        picture: string;
        url: string;
        publishDate: string;
        format: string;
        likes: number;
        comments: number;
    }[];
    strengths?: string[];
    weaknesses?: string[];
};

export type CompetitorPreviewProps = CompetitorProps[];

const CompetitorRow = (props: { data: CompetitorProps; defaultCollapsed?: boolean }) => {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState(props.defaultCollapsed || false);
    const competitor = props.data;

    return (
        <Paper elevation={2} sx={{ p: 2.5, mb: 1 }}>
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={2}>
                    <Avatar variant="rounded" src={competitor.profile.picture}>
                        {stringAvatar(competitor.profile.name)}
                    </Avatar>
                    <Stack
                        sx={{
                            minWidth: 0,
                            width: '100%',
                            flex: 1,
                        }}
                    >
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                width: '100%',
                                display: 'block',
                                minWidth: 0,
                            }}
                        >
                            {competitor.profile.name}{' '}
                            {competitor.profile.username && (
                                <Typography variant="caption">(@{competitor.profile.username})</Typography>
                            )}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ width: '100%', minWidth: 0 }}
                        >
                            <Trans
                                i18nKey="onboarding.chat.profile-followers"
                                values={{
                                    count: numberFormat(competitor.profile.followers),
                                }}
                            />
                        </Typography>
                    </Stack>
                </Stack>
                <IconButton onClick={() => setCollapsed(!collapsed)}>
                    {collapsed ? <ArrowDropDownRounded /> : <ArrowLeftRounded />}
                </IconButton>
            </Stack>
            {collapsed && (
                <Stack mt={2}>
                    <Stack gap={2}>
                        {(competitor.strengths?.length || competitor.weaknesses?.length) && (
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={6}>
                                {competitor.strengths && competitor.strengths.length > 0 && (
                                    <Stack width={1}>
                                        <Typography variant="subtitle2" color="success.main" mb={0.5}>
                                            {t('onboarding.competitors.strengths')}:
                                        </Typography>
                                        <Typography variant="caption" fontWeight={400}>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                {competitor.strengths.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ))}
                                            </ul>
                                        </Typography>
                                    </Stack>
                                )}
                                {competitor.weaknesses && competitor.weaknesses.length > 0 && (
                                    <Stack width={1}>
                                        <Typography variant="subtitle2" color="error.main" mb={0.5}>
                                            {t('onboarding.competitors.weaknesses')}:
                                        </Typography>
                                        <Typography variant="caption" fontWeight={400}>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                {competitor.weaknesses.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ))}
                                            </ul>
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Stack>
                    <Stack mt={3}>
                        <Typography variant="subtitle1">{t('onboarding.competitors.top-posts')}</Typography>
                        <Grid container spacing={2} pb={2} mt={2} columns={{ xs: 12 }}>
                            {competitor.topPosts.map((post) => (
                                <Grid key={post.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <PostCard
                                        post={{
                                            caption: post.caption,
                                            image: post.picture,
                                            publishDate: post.publishDate,
                                            format: post.format,
                                            metrics: [
                                                {
                                                    name: 'Likes',
                                                    value: numberFormat(post.likes),
                                                },
                                                {
                                                    name: 'Comments',
                                                    value: numberFormat(post.comments),
                                                },
                                            ],
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                </Stack>
            )}
        </Paper>
    );
};

export const CompetitorsPreview = (props: { data: CompetitorPreviewProps }) => {
    const { data } = props;

    return (
        <>
            {data.map((competitor, index) => (
                <CompetitorRow key={index} data={competitor} defaultCollapsed={index === 0} />
            ))}
        </>
    );
};
